import {Link, useNavigate, useParams} from "react-router-dom";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {SquadflixCollectionItem, SquadflixCollectionPagination, SquadflixCollectionResp} from "../types";
import {Spinner, TitlePage} from "../../../../../shared";
import useSquadflixNavigate from "../../../../../cap17drive/squadflixHome/useSquadflixNavigate";
import {cn, getLocalDateString, isMobileDevice} from "../../../../../../utils/utils";
import {useSelector} from "react-redux";
import users from "../../../../../users";
import CustomModalV2 from "../../../../../shared/custom-modal-v2/custom-modal-v2";
import Cap17driveSearcher from "../../../../../cap17drive/cap17driveSearcher/cap17driveSearcher";
import {toast} from "react-toastify";
import {ScrollModal,} from "../../../../../shared/scroll-modal";
import {ScrollModalApi} from "../../../../../shared/scroll-modal/view";
import {OptionButton} from "../../../../../shared/option-button";

import '../../../../../cap17drive/squadflix-collection-form/squadflix-collection-form.scss';
import './my-profile-collection-details.scss';
import ButtonV2 from "../../../../../shared/button-v2/button-v2";
import auth from "../../../../../../utils/authentication";
import {FormattedMessage} from "react-intl";
import {SquadflixCollectionsApi} from "../../../../../../apis/squadflix-collections/SquadflixCollectionsApi";
import ItemBackdropDetails from "../../../../../cap17drive/shared/item-backdrop-details/item-backdrop-details";

const MyProfileCollectionDetails = () => {

    const {id} = useParams();
    const [page, setPage] = useState(1);
    const navigate = useNavigate();
    const [collection, setCollection] = useState<SquadflixCollectionResp["collection"]>(null);
    const loadedPages = useRef<Record<number, SquadflixCollectionPagination>>({});
    const [items, setItems] = useState<SquadflixCollectionItem[]>([]);
    const isLoadingRef = useRef(false);
    const [squadflixSearcherOpen, setSquadflixSearcherOpen] = useState(false);
    const darkMode = useSelector(users.selectors.isDarkMode)
    const [loadingAddingItem, setLoadingAddingItem] = useState(false);
    const collectionOptionsModalRef = useRef<ScrollModalApi>(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [canSelectItems, setCanSelectItems] = useState(false);
    const [selectedItems, setSelectedItems] = useState<SquadflixCollectionItem[]>([]);
    const [renameCollectionOpen, setRenameCollectionOpen] = useState(false);
    const [newName, setNewName] = useState("");
    const [error, setError] = useState<string | null>(null);

    const squadflixKey = useMemo(() => {
        return (collection?.name ?? "unknown");
    }, [collection]);
    const isOwner = useMemo(() => {
        return auth.currentUser?.uid && collection?.uid === auth.currentUser.uid;
    }, [collection])
    const squadflixNav = useSquadflixNavigate(
        {
            navKey: <>
                <i className="fa-solid fa-bookmark fa-sm pe-1"></i>
                {squadflixKey}
            </>
        });

    const handleSaveRenameCollection = (newName: string) => {
        SquadflixCollectionsApi.updateCollectionName({
            collectionId: id!,
            name: newName.trim(),
        }).then(res => {
            if (res.success) {
                toast.success("Colección renombrada");
                setCollection(prev => ({
                    ...prev,
                    name: newName.trim(),
                }));
                collectionOptionsModalRef.current?.close();
                setRenameCollectionOpen(false);
            }
        }).catch(() => {
            toast.error("Error al renombrar la colección");
        });
    }

    const handleToggleSelectItems = (cancelDelete: boolean = true) => {
        if (canSelectItems && selectedItems.length > 0 && !cancelDelete) {
            console.log("Deleting items:", selectedItems);
            // Delete selected items
            Promise.all(selectedItems.map(item => {
                return SquadflixCollectionsApi.removeItemFromCollection({
                    collectionId: id!,
                    isMovie: item.isMovie,
                    tmdbId: item.tmdbId,
                    episode: -1,
                    season: -1,
                });
            })).then(responses => {
                const allSuccess = responses.every(res => res.success);
                if (allSuccess) {
                    toast.success("Ítems eliminados de la colección");
                    // Refresh collection
                    loadedPages.current = {};
                    setItems([]);
                    fetchCollectionPage(1);
                } else {
                    toast.error("Error al eliminar algunos ítems de la colección");
                }
            }).catch(() => {
                toast.error("Error al eliminar los ítems de la colección");
            }).finally(() => {
                setCanSelectItems(false);
                setSelectedItems([]);
            });
            return;
        }
        setCanSelectItems(!canSelectItems);
        setSelectedItems([]);
    }

    const handleAddFromSearcher = (item: any, isMovie: boolean) => {
        if (loadingAddingItem) return;
        setLoadingAddingItem(true);
        SquadflixCollectionsApi.addItemToCollection({
            collectionId: id,
            isMovie,
            tmdbId: item.id,
            episode: -1,
            season: -1,
        }).then(res => {
            if (res.success) {
                // Refresh collection
                loadedPages.current = {};
                setItems([]);
                fetchCollectionPage(1);
                toast.success("Ítem agregado a la colección");
            } else {
                toast.error("Error al agregar el ítem a la colección");
            }
        }).catch(e => {
            try {
                const error: { error: string } = JSON.parse(e.message);
                toast.error(error.error);
            } catch {
                toast.error("Error al agregar el ítem a la colección");
            }
        }).finally(() => {
            setLoadingAddingItem(false);
            setSquadflixSearcherOpen(false);
        });
    }

    const fetchCollectionPage = useCallback(async (pageNum: number) => {
        if (isLoadingRef.current) return;
        isLoadingRef.current = true;
        setError(null);
        return SquadflixCollectionsApi.getCollectionDetails({
            collectionId: id,
            page: pageNum
        }).then((res: SquadflixCollectionResp) => {
            const collection = res.collection;
            setNewName(collection.name);
            if (loadedPages.current[collection.result.page]) {
                return;
            }
            setCollection(collection);
            loadedPages.current[collection.result.page] = collection.result;
            setItems(prevItems => ([...prevItems, ...collection.result.items]));
        }).catch(e => {
            try {
                const error: { error: string } = JSON.parse(e.message);
                setError(error.error);
            } catch {
                setError("Error al cargar la colección");
            }
        }).finally(() => {
            isLoadingRef.current = false;
        })
    }, [id]);

    useEffect(() => {
        fetchCollectionPage(1);
    }, [fetchCollectionPage]);

    useEffect(() => {
        // if my-profile-collection-details-load-more in viewport, load next page
        const loadMoreObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    if (collection && collection.result.page < collection.result.totalPages) {
                        const nextPage = collection.result.page + 1;
                        fetchCollectionPage(nextPage);
                    }
                }
            });
        }, {
            root: null,
            rootMargin: '0px',
            threshold: 1.0
        });

        const loadMoreElement = document.getElementById('my-profile-collection-details-load-more');
        if (loadMoreElement) {
            loadMoreObserver.observe(loadMoreElement);
        }

        return () => {
            if (loadMoreElement) {
                loadMoreObserver.unobserve(loadMoreElement);
            }
        };
    })

    const isItemSelected = (item: SquadflixCollectionItem) => {
        return selectedItems.find(selected => selected.tmdbId === item.tmdbId
            && selected.isMovie === item.isMovie);
    }

    const handleSelectItem = (item: SquadflixCollectionItem) => {
        if (isItemSelected(item)) {
            setSelectedItems(prev => prev.filter(selected => !(selected.tmdbId === item.tmdbId
                && selected.isMovie === item.isMovie)));
        } else {
            setSelectedItems(prev => ([...prev, item]));
        }
    }

    const allItemsSelected = items.length > 0 && selectedItems.length === items.length;

    if (error) {
        return <div className={cn({"my-profile-collection-details-dark": darkMode})}>
            <TitlePage icon={undefined} title={
                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '1rem',
                    }}>
                        <Link to="../collections" className="my-profile-collection-details__back-btn">
                            <i className="fa-solid fa-chevron-left fa-lg"></i>
                        </Link>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0',
                        }}>
                            <div style={{
                                fontSize: '1.5rem',
                            }}>
                            </div>
                        </div>
                    </div>
                    {!id.includes('view-later-') && isOwner && <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '0.5rem',
                    }}>
                        {!isMobileDevice() && <>

                            <ButtonV2 width="50px" onClick={() => {
                                setSquadflixSearcherOpen(true);
                            }}>
                                <i className="fa-solid fa-plus"></i>
                            </ButtonV2>
                            <ButtonV2 width="50px"
                                      disabled={canSelectItems}
                                      onClick={() => handleToggleSelectItems()}>
                                <i className={cn("fa-solid fa-trash")}></i>
                            </ButtonV2>
                        </>}
                        <ButtonV2 width="50px" onClick={() => {
                            collectionOptionsModalRef.current?.open();
                        }}>
                            <i className="fa-solid fa-ellipsis-vertical"></i>
                        </ButtonV2>
                    </div>}
                </div>
            } rightOptions={undefined} titleClass={undefined}
                       leftClass={"col-12"} rightClass={undefined} disableScroll={true} hideSeparator={true}
                       fixedHeight={undefined} className={"mb-5 mt-2"} style={undefined}/>
            <div className="d-flex justify-content-center align-items-center" style={{height: '20vh'}}>
                <div className="text-danger">
                    <i className="fa-solid fa-triangle-exclamation fa-xl"></i> <FormattedMessage
                    id={"my-profile-collection-details.error." + error} defaultMessage={error}/>
                </div>
            </div>
        </div>
    }

    if (!collection) {
        return <div className="d-flex justify-content-center align-items-center" style={{height: '20vh'}}>
            <Spinner/>
        </div>
    }

    return <div className={cn({"my-profile-collection-details-dark": darkMode})}>
        <TitlePage icon={undefined} title={
            <div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '1rem',
                }}>
                    <Link to="../collections" className="my-profile-collection-details__back-btn">
                        <i className="fa-solid fa-chevron-left fa-lg"></i>
                    </Link>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0',
                    }}>
                        <div style={{
                            fontSize: '1.5rem',
                        }}>
                            {collection.name}
                            {collection.private && <i className="fa-solid fa-lock fa-xs ps-2"></i>}
                        </div>
                        <small className="text-muted">{collection.result.totalItems} elementos</small>
                    </div>
                </div>
                {!id.includes('view-later-') && isOwner && <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '0.5rem',
                }}>
                    {!isMobileDevice() && <>

                        <ButtonV2 width="50px" onClick={() => {
                            setSquadflixSearcherOpen(true);
                        }}>
                            <i className="fa-solid fa-plus"></i>
                        </ButtonV2>
                        <ButtonV2 width="50px"
                                  onClick={() => {
                                      if (!canSelectItems) {
                                          handleToggleSelectItems()
                                          return;
                                      }
                                      if (allItemsSelected) {
                                          setSelectedItems([]);
                                      } else {
                                          setSelectedItems([...items]);
                                      }
                                  }}>
                            {allItemsSelected ? <i className={cn("fa-regular fa-square-check fa-lg")}></i> :
                                <i className={cn("fa-regular fa-square fa-lg")}></i>}
                        </ButtonV2>
                    </>}
                    <ButtonV2 width="50px" onClick={() => {
                        collectionOptionsModalRef.current?.open();
                    }}>
                        <i className="fa-solid fa-ellipsis-vertical"></i>
                    </ButtonV2>
                </div>}
            </div>
        } rightOptions={undefined} titleClass={undefined}
                   leftClass={"col-12"} rightClass={undefined} disableScroll={true} hideSeparator={true}
                   fixedHeight={undefined} className={"mb-5 mt-2"} style={undefined}/>
        {
            items.length === 0 &&
            <div className="my-profile-collection-details-empty">
                <div className="text-muted" style={{
                    textAlign: 'center',
                    fontWeight: '500',
                }}>
                    <i className="fa-solid fa-circle-info"></i> ¡Coleción Vacía! Las peliculas y series que añadas a
                    esta colección aparecerán aquí.
                </div>
            </div>
        }
        {canSelectItems &&
            <div className="my-profile-collection-details--helper-select-items" style={{
                bottom: isMobileDevice() ? '80px' : '20px',
            }}>
                <strong>{selectedItems.length}</strong>&nbsp;ítem(s) seleccionado(s)
                <div className="d-flex flex-row justify-content-center mt-2 gap-3">
                    <ButtonV2 size="small" variant="danger" disabled={!selectedItems?.length}
                              onClick={() => handleToggleSelectItems(false)}>
                        Eliminar
                    </ButtonV2>
                    <ButtonV2 size="small" onClick={() => handleToggleSelectItems()} variant="primary">
                        Cancelar
                    </ButtonV2>
                </div>
            </div>
        }
        <div className="my-profile-collection-details" style={{
            paddingBottom: canSelectItems ? (50 + (isMobileDevice() ? 20 : 0)) : 0,
        }}>{
            items.map((item, i) => (
                <div key={item.tmdbId + "_" + item.isMovie.valueOf()} style={{
                    position: 'relative',
                    width: "100%",
                    display: 'flex',
                    justifyContent: 'center',
                }}>
                    <ItemBackdropDetails
                        tmdbSrc={item.item.poster_path} aspectRatio={"3/5"}
                        detailsTitle={item.item.title || item.item.name}
                        detailsInfo={getLocalDateString(item.addedAt)}
                        overlay={{
                            bottomRight:
                                <>
                                    <span>
                                    {item.item.vote_average.toFixed(1)} <i
                                        className="fa-solid fa-star text-warning"></i>
                                    </span>
                                    {item.notes && <>| <i className="fa-regular fa-note-sticky ps-2"/></>}
                                </>,
                            topRight: canSelectItems && (
                                <>
                                    {isItemSelected(item) ?
                                        <i className="fa-regular fa-square-check fa-lg"></i> :
                                        <i className="fa-regular fa-square fa-lg"></i>}
                                </>)
                        }}
                        onClickDetails={() => {
                            if (!canSelectItems) {
                                handleToggleSelectItems();
                            }
                            handleSelectItem(item);
                        }}
                        onClickPoster={() => {
                            if (canSelectItems) {
                                handleSelectItem(item);
                                return;
                            }
                            if (item.isMovie) {
                                squadflixNav.navigateToMovie(item.item);
                            } else {
                                squadflixNav.navigateToSerie(item.item);
                            }
                        }}/>
                </div>
            ))
        }
            <div id="my-profile-collection-details-load-more">
            </div>
        </div>
        {!id.includes('view-later-') && <>
            <CustomModalV2
                title="Añadir a la colección" open={squadflixSearcherOpen}
                primaryButton={{
                    text: loadingAddingItem ? "Añadiendo..." : "Cerrar",
                    variant: "primary",
                    disabled: loadingAddingItem,
                    onClick: () => {
                        if (loadingAddingItem) return;
                        setSquadflixSearcherOpen(false);
                    }
                }}
                onClose={() => {
                    if (loadingAddingItem) return;
                    setSquadflixSearcherOpen(false);
                }}>
                <div style={{
                    width: '100%', height: 'min(650px, calc(70dvh - 100px))', overflowY: 'auto'
                }}>
                    {squadflixSearcherOpen && !loadingAddingItem && <Cap17driveSearcher
                        onClickItem={({item, isMovie}) => {
                            handleAddFromSearcher(item, isMovie);
                        }}
                        hideTitle={true} navigateToSearch={false} onlyResults={false} onlyInput={false}
                        className={cn("my-profile-collection-details__items-searcher-modal")}/>}
                    {loadingAddingItem &&
                        <div className="d-flex justify-content-center align-items-center" style={{height: "100%"}}>
                            <Spinner/>
                        </div>
                    }
                </div>
            </CustomModalV2>
            <ScrollModal header={undefined} ref={collectionOptionsModalRef}>
                <OptionButton
                    onClick={() => setRenameCollectionOpen(true)}
                    text="Renombrar"
                    icon={<i className="fa-solid fa-pencil"></i>}/>
                {collection.private && <OptionButton
                    onClick={() => {
                        SquadflixCollectionsApi.updateCollectionPrivacy({
                            collectionId: id,
                            isPrivate: false
                        }).then(res => {
                            if (res.success) {
                                toast.success("Colección marcada como pública");
                                setCollection(prev => ({
                                    ...prev,
                                    private: false,
                                }));
                            } else {
                                toast.error("Error al actualizar la privacidad de la colección");
                            }
                        }).catch(() => {
                            toast.error("Error al actualizar la privacidad de la colección");
                        });
                        collectionOptionsModalRef.current?.close();
                    }}
                    text="Hacer pública"
                    icon={<i className="fa-solid fa-lock-open"></i>}/>}
                {!collection.private && <OptionButton
                    onClick={() => {
                        SquadflixCollectionsApi.updateCollectionPrivacy({
                            collectionId: id,
                            isPrivate: true
                        }).then(res => {
                            if (res.success) {
                                toast.success("Colección marcada como privada");
                                setCollection(prev => ({
                                    ...prev,
                                    private: true,
                                }));
                            } else {
                                toast.error("Error al actualizar la privacidad de la colección");
                            }
                        }).catch(() => {
                            toast.error("Error al actualizar la privacidad de la colección");
                        });
                        collectionOptionsModalRef.current?.close();
                    }}
                    text="Hacer privada"
                    icon={<i className="fa-solid fa-lock"></i>}/>}
                {isMobileDevice() && <OptionButton
                    onClick={() => {
                        setSquadflixSearcherOpen(true);
                        collectionOptionsModalRef.current?.close();
                    }}
                    text="Agregar ítem"
                    icon={<i className="fa-solid fa-plus"></i>}/>}
                {isMobileDevice() && <OptionButton
                    disabled={!items.length || canSelectItems}
                    onClick={() => {
                        collectionOptionsModalRef.current.close();
                        handleToggleSelectItems();
                    }}
                    text={"Eliminar ítems"}
                    icon={<i className={cn("fa-solid fa-trash",)}></i>}/>}
                <OptionButton
                    onClick={() => setConfirmDeleteOpen(true)}
                    text="Eliminar colección"
                    icon={<i
                        className="fa-solid fa-delete-left"></i>}/>
            </ScrollModal>
            <CustomModalV2 open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}
                           title="Eliminar colección"
                           primaryButton={{
                               text: "Eliminar",
                               variant: "danger",
                               onClick: () => {
                                   SquadflixCollectionsApi.deleteCollection({
                                       collectionId: id!,
                                   }).then(res => {
                                       if (res.success) {
                                           toast.success("Colección eliminada");
                                           navigate("/my-profile/collections");
                                       } else {
                                           toast.error("Error al eliminar la colección");
                                       }
                                   }).catch(() => {
                                       toast.error("Error al eliminar la colección");
                                   }).finally(() => {
                                       setConfirmDeleteOpen(false);
                                   });
                               }
                           }} secondaryButton={{
                text: "Cancelar",
                variant: "secondary",
                onClick: () => setConfirmDeleteOpen(false),
            }}>
                <div>
                    ¿Estás seguro de que deseas eliminar la colección "{collection.name}"? Esta acción no se puede
                    deshacer.
                </div>
            </CustomModalV2>
            <CustomModalV2
                open={renameCollectionOpen} onClose={() => setRenameCollectionOpen(false)}
                title="Renombrar colección"
                primaryButton={{
                    text: "Guardar",
                    variant: "primary",
                    onClick: () => {
                        handleSaveRenameCollection(newName);
                    }
                }}
                secondaryButton={{
                    text: "Cancelar",
                    variant: "secondary",
                    onClick: () => setRenameCollectionOpen(false),
                }}>
                <div>
                    <input type="text" placeholder="Nombre de la colección"
                           value={newName} onChange={e => setNewName(e.target.value)}
                           defaultValue={collection.name}
                           className="squadflix-collection-form-modal-content__input"
                           onKeyDown={(e) => {
                               if (e.key === 'Enter') {
                                   handleSaveRenameCollection((e.target as HTMLInputElement).value);
                               }
                           }}/>
                </div>
            </CustomModalV2>
        </>}
    </div>;
}

export default MyProfileCollectionDetails;
